<template>
  <div id="send-members-email">
    <v-overlay :value="ProgressLoadingFlag" color="#0a0f29" opacity="0.72">
      <div class="sme-overlay-loader">
        <v-progress-circular
          indeterminate
          size="64"
          width="5"
          color="#0097d7"
        ></v-progress-circular>
        <v-icon class="sme-overlay-icon" color="white">mdi-send</v-icon>
      </div>
    </v-overlay>

    <!-- Ambient background (decorative only) -->
    <div class="sme-ambient" aria-hidden="true">
      <span class="sme-glow sme-glow--navy"></span>
      <span class="sme-glow sme-glow--aqua"></span>
      <span class="sme-glow sme-glow--seafoam"></span>
    </div>

    <!-- Hero -->
    <section class="sme-hero sme-reveal" style="--d: 0ms">
      <div class="sme-hero__decor" aria-hidden="true">
        <span class="sme-orb sme-orb--1"></span>
        <span class="sme-orb sme-orb--2"></span>
        <span class="sme-ring sme-ring--1"></span>
        <span class="sme-ring sme-ring--2"></span>
        <span class="sme-particle sme-particle--1"></span>
        <span class="sme-particle sme-particle--2"></span>
        <span class="sme-particle sme-particle--3"></span>
        <span class="sme-particle sme-particle--4"></span>
        <span class="sme-particle sme-particle--5"></span>
        <svg class="sme-hero__flight" viewBox="0 0 400 160" fill="none">
          <path
            d="M10 140 C 120 140, 160 40, 260 60 S 370 30, 390 12"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-dasharray="4 8"
            stroke-linecap="round"
          />
          <path
            class="sme-hero__flight-light"
            d="M10 140 C 120 140, 160 40, 260 60 S 370 30, 390 12"
            pathLength="100"
            stroke="currentColor"
            stroke-width="3"
            stroke-linecap="round"
          />
        </svg>
      </div>

      <div class="sme-hero__grid">
        <div class="sme-hero__copy">
          <div class="sme-hero__eyebrow">
            <span class="sme-hero__icon">
              <v-icon color="white" size="22">mdi-email</v-icon>
            </span>
            <span>Communication <em>Studio</em></span>
          </div>
          <h1 class="sme-hero__title">{{ PageTitle }}</h1>
          <span class="sme-hero__accent" aria-hidden="true"></span>
          <p class="sme-hero__desc" v-if="PageDescription">
            {{ PageDescription }}
          </p>
        </div>

        <div class="sme-hero__side">
          <!-- Email credits -->
          <div class="sme-credit sme-reveal" style="--d: 120ms">
            <span class="sme-credit__shine" aria-hidden="true"></span>
            <svg
              class="sme-credit__rings"
              viewBox="0 0 120 120"
              fill="none"
              aria-hidden="true"
            >
              <circle
                cx="60"
                cy="60"
                r="56"
                stroke="currentColor"
                stroke-width="1"
              />
              <circle
                cx="60"
                cy="60"
                r="40"
                stroke="currentColor"
                stroke-width="1"
                stroke-dasharray="3 6"
              />
            </svg>
            <div class="sme-credit__icon">
              <v-icon color="#0a0f29" size="26"
                >mdi-email-multiple-outline</v-icon
              >
            </div>
            <div class="sme-credit__body">
              <div class="sme-credit__label">Email Credits</div>
              <div class="sme-credit__value">{{ EmailCredits }}</div>
              <div class="sme-credit__hint">Available for sending</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <div class="d-flex justify-content-center my-4" v-if="LoadingFlag">
      <b-spinner variant="primary" type="grow" label="Loading..."></b-spinner>
    </div>

    <v-form
      ref="form1"
      v-model="valid1"
      lazy-validation
      v-on:submit.prevent="submitForm"
      class="sme-form"
    >
      <!-- Step 01: Targeting -->
      <section
        class="sme-panel sme-panel--targeting sme-reveal"
        style="--d: 200ms"
      >
        <header class="sme-panel__head">
          <div class="sme-section-title">
            <span class="sme-step">01</span>
            <span class="sme-section-title__icon">
              <v-icon size="20" color="#3a67b1">{{
                Mode == 2 ? "mdi-tune" : "mdi-account-group"
              }}</v-icon>
            </span>
            <div>
              <h2 v-if="Mode == 2">Audience Configuration</h2>
              <h2 v-else>Recipient Targeting</h2>
              <p v-if="Mode == 2">
                Choose the organisation type and who should receive this email.
              </p>
              <p v-else>
                Filter members by year, organisation, zone and designation.
              </p>
            </div>
          </div>

          <div class="sme-mode" :class="'sme-mode--' + Mode">
            <span class="sme-mode__indicator" aria-hidden="true"></span>
            <v-radio-group v-model="Mode" row hide-details class="mt-0">
              <v-radio
                v-for="item in ModeOptions"
                :key="item.value"
                :label="item.text"
                :value="item.value"
              >
                <template v-slot:label>
                  <v-icon size="18" class="sme-mode__icon">{{
                    item.value == 1
                      ? "mdi-account-group-outline"
                      : "mdi-shape-outline"
                  }}</v-icon>
                  {{ item.text }}
                </template>
              </v-radio>
            </v-radio-group>
          </div>
        </header>

        <p class="sme-required-note">
          <span class="sme-req">*</span> indicates required field
        </p>

        <v-row class="sme-fields">
          <v-col cols="12" sm="6" md="3" lg="3" class="sme-field">
            <label class="sme-label"><span class="sme-req">*</span> Year</label>
            <v-autocomplete
              :reduce="(option) => option.value"
              :items="JciYearCodeOptions"
              :rules="JciYearCodeRules"
              v-model="JciYearCode"
              dense
              clearable
              required
              outlined
            ></v-autocomplete>
          </v-col>
          <template v-if="Mode == 1">
            <v-col cols="12" sm="6" md="3" lg="3" class="sme-field">
              <label class="sme-label">
                <span class="sme-req">*</span> Organization Type
              </label>
              <v-select
                :reduce="(option) => option.value"
                :items="OrganizationTypeOptions1"
                :rules="OrganizationTypeRules1"
                v-model="OrganizationType1"
                dense
                clearable
                required
                outlined
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="3" lg="3" class="sme-field">
              <label class="sme-label">
                <span class="sme-req">*</span> Member Type
              </label>
              <v-select
                :reduce="(option) => option.value"
                v-model="MemberType"
                :items="MemberTypeOptions"
                :rules="MemberTypeRules"
                :loading="MemberTypeOptionsLoading"
                dense
                clearable
                required
                outlined
              ></v-select>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              md="3"
              lg="3"
              class="sme-field"
              v-if="MemberType == 1"
            >
              <label class="sme-label"
                ><span class="sme-req">*</span> Zone</label
              >
              <v-autocomplete
                @change="getLomCodeOptions"
                :reduce="(option) => option.value"
                :loading="ZoneCodeOptionsLoading"
                :items="ZoneCodeOptions"
                :rules="ZoneCodeRules"
                v-model="ZoneCode"
                dense
                clearable
                required
                outlined
              ></v-autocomplete>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              md="3"
              lg="3"
              class="sme-field"
              v-if="MemberType == 1"
            >
              <label class="sme-label">LO</label>
              <v-autocomplete
                :reduce="(option) => option.value"
                :loading="LomCodeOptionsLoading"
                :items="LomCodeOptions"
                :rules="LomCodeRules"
                v-model="LomCode"
                dense
                clearable
                outlined
                multiple
                chips
                deletable-chips
              ></v-autocomplete>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              md="3"
              lg="3"
              class="sme-field"
              v-if="MemberType == 1"
            >
              <label class="sme-label">Half</label>
              <v-select
                :reduce="(option) => option.value"
                :items="YearHalfOptions"
                :rules="YearHalfRules"
                v-model="YearHalf"
                dense
                clearable
                required
                outlined
              ></v-select>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              md="3"
              lg="3"
              class="sme-field"
              v-if="MemberType == 2"
            >
              <label class="sme-label">
                <span class="sme-req">*</span> Governing Board Type
              </label>
              <v-select
                @change="getDesignationCodeOptions"
                :reduce="(option) => option.value"
                :items="OrganizationTypeOptions"
                :rules="OrganizationTypeRules"
                v-model="OrganizationType"
                dense
                clearable
                required
                outlined
              ></v-select>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              md="3"
              lg="3"
              class="sme-field"
              v-if="
                (MemberType == 2 && OrganizationType == 2) ||
                OrganizationType == 3
              "
            >
              <label class="sme-label">Area</label>
              <v-autocomplete
                @change="getDesignationZoneCodeOptions"
                :reduce="(option) => option.value"
                :loading="DesignationAreaCodeOptionsLoading"
                :items="DesignationAreaCodeOptions"
                :rules="DesignationAreaCodeRules"
                v-model="DesignationAreaCode"
                dense
                clearable
                required
                outlined
              ></v-autocomplete>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              md="3"
              lg="3"
              class="sme-field"
              v-if="
                (MemberType == 2 && OrganizationType == 2) ||
                OrganizationType == 3
              "
            >
              <label class="sme-label">Zone</label>
              <v-autocomplete
                @change="getDesignationLomCodeOptions"
                :reduce="(option) => option.value"
                :loading="DesignationZoneCodeOptionsLoading"
                :items="DesignationZoneCodeOptions"
                :rules="DesignationZoneCodeRules"
                v-model="DesignationZoneCode"
                dense
                clearable
                required
                outlined
              ></v-autocomplete>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              md="3"
              lg="3"
              class="sme-field"
              v-if="MemberType == 2 && OrganizationType == 3"
            >
              <label class="sme-label">LO</label>
              <v-autocomplete
                :reduce="(option) => option.value"
                :loading="DesignationLomCodeOptionsLoading"
                :items="DesignationLomCodeOptions"
                :rules="DesignationLomCodeRules"
                v-model="DesignationLomCode"
                dense
                clearable
                required
                outlined
              ></v-autocomplete>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              md="3"
              lg="3"
              class="sme-field"
              v-if="MemberType == 2"
            >
              <label class="sme-label">Designation</label>
              <v-autocomplete
                :reduce="(option) => option.value"
                :loading="DesignationCodeOptionsLoading"
                :items="DesignationCodeOptions"
                :rules="DesignationCodeRules"
                v-model="DesignationCode"
                dense
                clearable
                outlined
                multiple
                chips
                deletable-chips
              ></v-autocomplete>
            </v-col>
          </template>
          <template v-if="Mode == 2">
            <v-col cols="12" sm="6" md="3" lg="3" class="sme-field">
              <label class="sme-label"
                ><span class="sme-req">*</span> Type</label
              >
              <v-select
                :items="TypeOptions"
                :rules="TypeRules"
                v-model="TypeCode"
                dense
                clearable
                required
                outlined
              ></v-select>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              md="3"
              lg="3"
              class="sme-field"
              v-if="TypeCode != ''"
            >
              <label class="sme-label">
                <span class="sme-req">*</span> Send To
              </label>
              <v-select
                :items="SendToOptions"
                :rules="SendToRules"
                v-model="SendTo"
                dense
                clearable
                required
                outlined
              ></v-select>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              md="3"
              lg="3"
              class="sme-field"
              v-if="[2, 4].includes(SendTo)"
            >
              <label class="sme-label">Zone</label>
              <v-autocomplete
                :loading="ZoneCodeOptionsLoading"
                :items="ZoneCodeOptions"
                v-model="ZoneCodes"
                dense
                clearable
                outlined
                multiple
                chips
                deletable-chips
              ></v-autocomplete>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              md="3"
              lg="3"
              class="sme-field"
              v-if="[3, 5].includes(SendTo)"
            >
              <label class="sme-label"
                ><span class="sme-req">*</span> Zone</label
              >
              <v-autocomplete
                @change="getTypeZoneChange"
                :loading="ZoneCodeOptionsLoading"
                :items="ZoneCodeOptions"
                :rules="TypeZoneRules"
                v-model="ZoneCode"
                dense
                clearable
                outlined
              ></v-autocomplete>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              md="3"
              lg="3"
              class="sme-field"
              v-if="SendTo == 3"
            >
              <label class="sme-label">LO</label>
              <v-autocomplete
                :loading="LomCodeOptionsLoading"
                :items="LomCodeOptions"
                v-model="LomCode"
                dense
                clearable
                outlined
                multiple
                chips
                deletable-chips
              ></v-autocomplete>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              md="3"
              lg="3"
              class="sme-field"
              v-if="SendTo == 5"
            >
              <label class="sme-label">Table</label>
              <v-autocomplete
                :loading="TableCodeOptionsLoading"
                :items="TableCodeOptions"
                v-model="TableCode"
                dense
                clearable
                outlined
                multiple
                chips
                deletable-chips
              ></v-autocomplete>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              md="3"
              lg="3"
              class="sme-field"
              v-if="[3, 5].includes(SendTo)"
            >
              <label class="sme-label">
                <span class="sme-req">*</span> Recipients
              </label>
              <v-select
                :items="RecipientOptions"
                :rules="RecipientRules"
                v-model="Recipient"
                dense
                clearable
                required
                outlined
              ></v-select>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              md="3"
              lg="3"
              class="sme-field"
              v-if="SendTo == 3 && Recipient == 2"
            >
              <label class="sme-label">Half</label>
              <v-select
                :items="YearHalfOptions"
                v-model="YearHalf"
                dense
                clearable
                outlined
              ></v-select>
            </v-col>
          </template>
        </v-row>
      </section>

      <!-- Step 02: Compose -->
      <section
        class="sme-panel sme-panel--compose sme-reveal"
        style="--d: 300ms"
      >
        <header class="sme-panel__head">
          <div class="sme-section-title">
            <span class="sme-step">02</span>
            <span class="sme-section-title__icon">
              <v-icon size="20" color="#3a67b1">mdi-feather</v-icon>
            </span>
            <div>
              <h2>Compose Message</h2>
              <p>Set the sender details, subject and the body of your email.</p>
            </div>
          </div>
        </header>

        <div class="sme-subgroup">
          <div class="sme-subgroup__title">
            <v-icon size="16">mdi-card-account-mail-outline</v-icon>
            Email identity
          </div>
          <v-row class="sme-fields">
            <v-col cols="12" class="sme-field sme-field--subject">
              <label class="sme-label">
                <span class="sme-req">*</span> Subject
              </label>
              <v-text-field
                v-model="Subject"
                :rules="SubjectRules"
                placeholder="Subject"
                required
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="6" lg="6" class="sme-field">
              <label class="sme-label">
                <span class="sme-req">*</span> Reply To Email ID
              </label>
              <v-text-field
                v-model="ReplyToEmailId"
                :rules="ReplyToEmailIdRules"
                placeholder="Reply to email id"
                hint="Enter the Email"
                outlined
                dense
                prepend-inner-icon="mdi-reply-outline"
              ></v-text-field>
            </v-col>
            <v-col
              cols="12"
              sm="6"
              md="6"
              lg="6"
              class="sme-field"
              v-if="CcMailFlag"
            >
              <label class="sme-label">
                CC Mails <span class="sme-badge">optional</span>
              </label>
              <v-text-field
                v-model="CCMails"
                :rules="CCMailsRules"
                placeholder="(ex: abc@mail.com,xyz@mail.com,...,etc)"
                required
                outlined
                dense
                prepend-inner-icon="mdi-account-multiple-plus-outline"
              ></v-text-field>
            </v-col>
          </v-row>
        </div>

        <div class="sme-subgroup">
          <div class="sme-subgroup__title">
            <v-icon size="16">mdi-text-box-edit-outline</v-icon>
            <span><span class="sme-req">*</span> Email content</span>
          </div>

          <div class="sme-vars">
            <div class="sme-vars__head">
              <span class="sme-vars__icon">
                <v-icon size="18" color="#0097d7">mdi-auto-fix</v-icon>
              </span>
              <div>
                <div class="sme-vars__title">Personalization variables</div>
                <div class="sme-vars__sub">
                  Kindly note, the following variables can be used in email:
                </div>
              </div>
            </div>
            <ul class="sme-vars__list">
              <li class="sme-var">
                <span class="sme-var__label">Name</span>
                <code class="sme-var__code">#name#</code>
              </li>
              <li class="sme-var">
                <span class="sme-var__label">Designation</span>
                <code class="sme-var__code">#designation#</code>
              </li>
              <li class="sme-var">
                <span class="sme-var__label">LO name</span>
                <code class="sme-var__code">#loname#</code>
              </li>
              <li class="sme-var">
                <span class="sme-var__label">Zone name</span>
                <code class="sme-var__code">#zonename#</code>
              </li>
            </ul>
          </div>

          <div class="sme-editor">
            <ckeditor v-model="Content" :config="editorConfig"></ckeditor>
          </div>
          <!-- <tiptap-vuetify
            v-model="Content"
            :extensions="extensions"
            :toolbar-attributes="{ color: 'blue' }"
          /> -->
        </div>
      </section>

      <!-- Step 03: Attachments & send -->
      <section
        class="sme-panel sme-panel--dispatch sme-reveal"
        style="--d: 400ms"
      >
        <header class="sme-panel__head">
          <div class="sme-section-title">
            <span class="sme-step">03</span>
            <span class="sme-section-title__icon">
              <v-icon size="20" color="#3a67b1">mdi-paperclip</v-icon>
            </span>
            <div>
              <h2>Attachments &amp; Dispatch</h2>
              <p>Attach up to two files, then send your email.</p>
            </div>
          </div>
        </header>

        <v-row class="sme-fields">
          <v-col cols="12" sm="6" md="6" lg="6" class="sme-field">
            <div
              class="sme-attach"
              :class="{
                'sme-attach--filled': UploadedFile && UploadedFile.name,
              }"
            >
              <div class="sme-attach__head">
                <span class="sme-attach__icon">
                  <v-icon size="22" color="#3a67b1"
                    >mdi-file-upload-outline</v-icon
                  >
                </span>
                <div class="sme-attach__meta">
                  <div class="sme-attach__title">
                    Attachment 1 <span class="sme-badge">optional</span>
                  </div>
                  <div class="sme-attach__hint">
                    Maximum file upload size is 5MB
                  </div>
                </div>
              </div>
              <!-- accept="application/pdf" -->
              <v-file-input
                :clearable="false"
                truncate-length="25"
                placeholder="Upload PDF File"
                v-model="UploadedFile"
                prepend-icon="mdi-paperclip"
              >
                <template v-slot:append-outer>
                  <v-icon color="red" @click="UploadedFile = {}">
                    mdi-delete
                  </v-icon>
                </template>
              </v-file-input>
            </div>
          </v-col>
          <v-col cols="12" sm="6" md="6" lg="6" class="sme-field">
            <div
              class="sme-attach"
              :class="{
                'sme-attach--filled': UploadedFile1 && UploadedFile1.name,
              }"
            >
              <div class="sme-attach__head">
                <span class="sme-attach__icon">
                  <v-icon size="22" color="#3a67b1"
                    >mdi-file-upload-outline</v-icon
                  >
                </span>
                <div class="sme-attach__meta">
                  <div class="sme-attach__title">
                    Attachment 2 <span class="sme-badge">optional</span>
                  </div>
                  <div class="sme-attach__hint">
                    Maximum file upload size is 5MB
                  </div>
                </div>
              </div>
              <!-- accept="application/pdf" -->
              <v-file-input
                :clearable="false"
                truncate-length="25"
                placeholder="Upload PDF File"
                v-model="UploadedFile1"
                prepend-icon="mdi-paperclip"
              >
                <template v-slot:append-outer>
                  <v-icon color="red" @click="UploadedFile1 = {}">
                    mdi-delete
                  </v-icon>
                </template>
              </v-file-input>
            </div>
          </v-col>
        </v-row>

        <div class="sme-send-bar">
          <div class="sme-send-bar__copy">
            <v-icon size="20" color="#56bda3">mdi-shield-check-outline</v-icon>
            <span> Review the recipients and content before sending. </span>
          </div>
          <v-btn
            :disabled="!valid1"
            @click.prevent="submitForm"
            :loading="SubmitFlag"
            large
            depressed
            class="sme-send-btn"
          >
            <span class="sme-send-btn__shine" aria-hidden="true"></span>
            Send Email
            <v-icon right size="20" class="sme-send-btn__icon">mdi-send</v-icon>
            <template v-slot:loader>
              <v-progress-circular
                indeterminate
                size="20"
                width="2"
                color="white"
                class="mr-2"
              ></v-progress-circular>
              Sending…
            </template>
          </v-btn>
        </div>
      </section>
    </v-form>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";
import {
  TiptapVuetify,
  Heading,
  Bold,
  Italic,
  Strike,
  Underline,
  Code,
  Paragraph,
  BulletList,
  OrderedList,
  ListItem,
  Link,
  Blockquote,
  HardBreak,
  HorizontalRule,
  History,
} from "tiptap-vuetify";

export default {
  mixins: [common],
  components: { TiptapVuetify },
  props: {},
  data() {
    return {
      valid1: true,
      ProgressLoadingFlag: false,
      LoadingFlag: false,
      SubmitFlag: false,

      Subject: "",
      SubjectRules: [(v) => !!v || "Subject is required"],

      UploadedFile: {},
      UploadedFile1: {},

      Content: "",
      ContentRules: [(v) => !!v || "Content is required"],
      editorConfig: {
        versionCheck: false,
        toolbar: [
          { name: "styles", items: ["Format", "Styles", "Font", "FontSize"] },
          {
            name: "basicstyles",
            groups: ["basicstyles", "cleanup"],
            items: [
              "Bold",
              "Italic",
              "Underline",
              "Strike",
              "Subscript",
              "Superscript",
              "-",
              "RemoveFormat",
            ],
          },
          {
            name: "paragraph",
            groups: ["list", "indent", "blocks", "align", "bidi"],
            items: [
              "NumberedList",
              "BulletedList",
              "-",
              "Outdent",
              "Indent",
              "-",
              "Blockquote",
              "CreateDiv",
              "-",
              "JustifyLeft",
              "JustifyCenter",
              "JustifyRight",
              "JustifyBlock",
              "-",
              "BidiLtr",
              "BidiRtl",
              "Language",
            ],
          },
          { name: "links", items: ["Link", "Unlink", "Anchor"] },
          {
            name: "insert",
            items: [
              "Image",
              "Flash",
              "Table",
              "HorizontalRule",
              "Smiley",
              "SpecialChar",
              "PageBreak",
              "Iframe",
            ],
          },
          {
            name: "clipboard",
            groups: ["clipboard", "undo"],
            items: [
              "Cut",
              "Copy",
              "Paste",
              "PasteText",
              "PasteFromWord",
              "-",
              "Undo",
              "Redo",
            ],
          },
          {
            name: "editing",
            groups: ["find", "selection", "spellchecker"],
            items: ["Find", "Replace", "-", "SelectAll", "-", "Scayt"],
          },
          {
            name: "forms",
            items: [
              "Form",
              "Checkbox",
              "Radio",
              "TextField",
              "Textarea",
              "Select",
              "Button",
              "ImageButton",
              "HiddenField",
            ],
          },
          { name: "colors", items: ["TextColor", "BGColor"] },
          { name: "tools", items: ["Maximize", "ShowBlocks"] },
          {
            name: "document",
            groups: ["mode", "document", "doctools"],
            items: [
              "Source",
              "-",
              "Save",
              "NewPage",
              "Preview",
              "Print",
              "-",
              "Templates",
            ],
          },
        ],
      },

      CCMails: "",
      CCMailsRules: [],
      CcMailFlag: false,

      ReplyToEmailId: "",
      ReplyToEmailIdRules: [
        (v) => !!v || "Reply to e-mail id is required",
        (v) => /.+@.+\..+/.test(v) || "Reply to e-mail id must be valid",
      ],

      MemberType: "",
      MemberTypeRules: [(v) => !!v || "Member Type is required"],
      MemberTypeOptions: [
        { value: "", text: "Select Member Type" },
        { value: 1, text: "JCI India Members" },
        { value: 2, text: "Board Members" },
        { value: 3, text: "JAC Members" },
      ],
      MemberTypeOptionsLoading: false,

      JciYearCodeFlag: false,
      JciYearCodeRules: [(v) => !!v || "Year is required"],
      JciYearCode: "",
      JciYearCodeOptions: [],

      ZoneCode: "",
      ZoneCodeRules: [(v) => !!v || "Zone is required"],
      ZoneCodeOptions: [],
      ZoneCodeOptionsLoading: false,

      // Multiple zones for By Type ZGB / Members
      ZoneCodes: [],

      LomCode: [],
      LomCodeRules: [],
      LomCodeOptions: [],
      LomCodeOptionsLoading: false,

      YearHalf: "",
      YearHalfRules: [],
      YearHalfOptions: [
        { value: "", text: "Select Half" },
        { value: "First", text: "First Half" },
        { value: "Second", text: "Second Half" },
      ],

      OrganizationType: "",
      OrganizationTypeRules: [(v) => !!v || "Governing Board Type is required"],
      OrganizationTypeOptions: [
        { value: "", text: "--Select--" },
        { value: 1, text: "National" },
        { value: 2, text: "Zone" },
        { value: 3, text: "LO" },
      ],
      OrganizationTypeOptionsLoading: false,

      OrganizationType1: "",
      OrganizationTypeRules1: [(v) => !!v || "Organization Type is required"],
      OrganizationTypeOptions1: [],
      OrganizationTypeOptionsLoading1: false,

      DesignationAreaCode: "",
      DesignationAreaCodeOptions: [],
      DesignationAreaCodeRules: [],
      DesignationAreaCodeOptionsLoading: false,

      DesignationZoneCode: "",
      DesignationZoneCodeRules: [],
      DesignationZoneCodeOptions: [],
      DesignationZoneCodeOptionsLoading: false,

      DesignationLomCode: "",
      DesignationLomCodeRules: [],
      DesignationLomCodeOptions: [],
      DesignationLomCodeOptionsLoading: false,

      DesignationCode: [],
      DesignationCodeRules: [],
      DesignationCodeOptions: [],
      DesignationCodeOptionsLoading: false,

      // Mode 1=Existing; 2=By Type
      Mode: 1,
      ModeOptions: [
        { value: 1, text: "Existing" },
        { value: 2, text: "By Type" },
      ],

      // Type 1=JCI India; 2=JAC; 3=JCOM
      TypeCode: "",
      TypeRules: [(v) => !!v || "Type is required"],
      TypeOptions: [
        { value: 1, text: "JCI India" },
        { value: 2, text: "JAC" },
        { value: 3, text: "JCOM" },
      ],

      // Send To 1=NGB; 2=ZGB; 3=LO; 4=Members; 5=Table
      SendTo: "",
      SendToRules: [(v) => !!v || "Send To is required"],

      // Recipient 1=Governing Board (LGB/TGB); 2=Members
      Recipient: "",
      RecipientRules: [(v) => !!v || "Recipients is required"],

      TableCode: [],
      TableCodeOptions: [],
      TableCodeOptionsLoading: false,

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      CurrentYearId: 0,
      JciYearCode: "",
      YearName: "",
      EmailCredits: 0,

      extensions: [
        History,
        Blockquote,
        Bold,
        Italic,
        Underline,
        ListItem,
        BulletList,
        OrderedList,
        [
          Heading,
          {
            options: {
              levels: [1, 2, 3],
            },
          },
        ],
        HorizontalRule,
        Paragraph,
        HardBreak,
      ],
    };
  },
  computed: {
    SendToOptions() {
      var TypeCode = this.TypeCode;
      var options = [
        { value: 1, text: "NGB" },
        { value: 2, text: "ZGB" },
      ];
      if (TypeCode == 1) {
        options.push({ value: 3, text: "LO" });
      } else if (TypeCode == 2) {
        options.push({ value: 4, text: "Members" });
      } else if (TypeCode == 3) {
        options.push({ value: 5, text: "Table" });
      }
      return options;
    },
    RecipientOptions() {
      return [
        { value: 1, text: this.TypeCode == 3 ? "TGB" : "LGB" },
        { value: 2, text: "Members" },
      ];
    },
    TypeZoneRules() {
      return [3, 5].includes(this.SendTo)
        ? [(v) => !!v || "Zone is required"]
        : [];
    },
  },
  watch: {
    Mode: function () {
      console.log("watch Mode");
      this.MemberType = "";
      this.OrganizationType1 = "";
      this.TypeCode = "";
    },
    TypeCode: function () {
      console.log("watch TypeCode");
      this.SendTo = "";
    },
    SendTo: function () {
      console.log("watch SendTo");
      this.ZoneCode = "";
      this.ZoneCodes = [];
      this.LomCode = [];
      this.TableCode = [];
      this.Recipient = "";
      this.YearHalf = "";
    },
    TableCodeOptions: function () {
      console.log("watch TableCodeOptions");
      this.TableCodeOptionsLoading = false;
    },
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        var flag = tr.PagePermissionFlag;
        console.log("PagePermissionFlag=" + flag);

        if (!flag) {
          this.$router.push("/access-denied");
        }

        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });
        this.JciYearCode = CurrentJciYearId;

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;
        this.CurrentYearId = CurrentJciYearId;

        var EmailCredits = parseInt(tr.EmailCredits);
        console.log({ EmailCredits });
        this.EmailCredits = EmailCredits;

        this.getJciYearCodeOptions();
        this.getOrganizationTypeOptions();
        this.getZoneCodeOptions();
        this.getDesignationAreaCodeOptions();
      }
    },
    MemberType: function () {
      console.log("watch MemberType");
      var MemberType = this.MemberType;
      console.log("MemberType=" + MemberType);

      this.ZoneCode = "";
      this.LomCode = [];
      this.YearHalf = "";
      this.OrganizationType = "";
      this.DesignationCode = [];
      this.DesignationZoneCode = "";
      this.DesignationLomCode = "";
    },
    JciYearCode: function () {
      console.log("Watch JciYearCode called");
      this.JciYearCodeFlag = false;
    },
    OrganizationType1: function () {
      console.log("Watch JciYearCode called");
      this.OrganizationTypeOptionsLoading1 = false;
    },
    ZoneCodeOptions: function () {
      console.log("watch ZoneCodeOptions");
      this.ZoneCodeOptionsLoading = false;
    },
    LomCodeOptions: function () {
      console.log("watch LomCodeOptions");
      this.LomCodeOptionsLoading = false;
    },
    DesignationAreaCodeOptions: function () {
      console.log("watch DesignationAreaCodeOptions");
      this.DesignationAreaCodeOptionsLoading = false;
    },
    DesignationZoneCodeOptions: function () {
      console.log("watch DesignationZoneCodeOptions");
      this.DesignationZoneCodeOptionsLoading = false;
    },
    DesignationLomCodeOptions: function () {
      console.log("watch DesignationLomCodeOptions");
      this.DesignationLomCodeOptionsLoading = false;
    },

    DesignationCodeOptions: function () {
      console.log("watch DesignationCodeOptions");
      this.DesignationCodeOptionsLoading = false;
    },
  },
  mounted() {},
  methods: {
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "communication",
        Action: "report_1",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    getJciYearCodeOptions() {
      console.log("getJciYearCodeOptions called");
      var selectbox1_source = "JciYearCode";
      var selectbox1_destination = "JciYearCodeOptions";
      var selectbox1_url = "api/jci-year/options";
      var selectbox1_conditions_array = {
        UserInterface: 5,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    getZoneCodeOptions() {
      console.log("getZoneCodeOptions called");
      var yearCode = this.JciYearCode;
      console.log("yearCode=" + yearCode);
      this.ZoneCodeOptionsLoading = true;
      if (yearCode != "") {
        var selectbox1_source = "ZoneCode";
        var selectbox1_destination = "ZoneCodeOptions";
        var selectbox1_url = "api/year-wise-lom/zone-options";
        var selectbox1_conditions_array = {
          UserInterface: 2,
          YearCode: yearCode,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      }
    },
    getLomCodeOptions() {
      console.log("getLomCodeOptions called");
      var YearCode = this.JciYearCode;
      var ZoneCode = this.ZoneCode;
      this.LomCodeOptionsLoading = true;
      console.log("YearCode=" + YearCode + ", ZoneCode=" + ZoneCode);
      if (YearCode != "" && ZoneCode != "") {
        var selectbox1_source = "LomCode";
        var selectbox1_destination = "LomCodeOptions";
        var selectbox1_url = "api/year-wise-lom/lom-options";
        var selectbox1_conditions_array = {
          UserInterface: 1,
          YearCode: YearCode,
          ZoneCode: ZoneCode,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      }
    },
    getTypeZoneChange() {
      console.log("getTypeZoneChange called");
      this.LomCode = [];
      this.TableCode = [];
      if (this.ZoneCode == "" || this.ZoneCode == null) {
        this.LomCodeOptions = [];
        this.TableCodeOptions = [];
      } else if (this.SendTo == 3) {
        this.getLomCodeOptions();
      } else if (this.SendTo == 5) {
        this.getTableCodeOptions();
      }
    },
    getTableCodeOptions() {
      console.log("getTableCodeOptions called");
      var ZoneCode = this.ZoneCode;
      console.log("ZoneCode=" + ZoneCode);
      if (ZoneCode != "" && ZoneCode != null) {
        this.TableCodeOptionsLoading = true;
        var selectbox1_source = "TableCode";
        var selectbox1_destination = "TableCodeOptions";
        var selectbox1_url = "api/jcom/table/options";
        var selectbox1_conditions_array = {
          UserInterface: 2,
          Zone: ZoneCode,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      } else {
        this.TableCodeOptions = [];
      }
    },
    getDesignationAreaCodeOptions() {
      console.log("getDesignationAreaCodeOptions called");
      this.DesignationAreaCodeOptionsLoading = true;
      var selectbox1_source = "DesignationAreaCode";
      var selectbox1_destination = "DesignationAreaCodeOptions";
      var selectbox1_url = "api/year-wise-lom/area-options";
      var selectbox1_conditions_array = {
        UserInterface: 3,
        YearCode: this.JciYearCode,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    getDesignationZoneCodeOptions() {
      console.log("getDesignationZoneCodeOptions called");

      var yearCode = this.JciYearCode;
      var areaCode = this.DesignationAreaCode;
      console.log("yearCode=" + yearCode + ", areaCode=" + areaCode);
      if (yearCode != "" && areaCode != "") {
        this.DesignationZoneCode = "";
        this.DesignationZoneCodeOptionsLoading = true;
        this.tableData2 = [];
        var selectbox1_source = "DesignationZoneCode";
        var selectbox1_destination = "DesignationZoneCodeOptions";
        var selectbox1_url = "api/year-wise-zone-options";
        var selectbox1_conditions_array = {
          UserInterface: 6,
          AreaCode: areaCode,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      }
    },
    getDesignationLomCodeOptions() {
      console.log("getDesignationLomCodeOptions called");
      this.DesignationLomCode = "";

      var YearCode = this.JciYearCode;
      var ZoneCode = this.DesignationZoneCode;
      console.log("YearCode=" + YearCode + ", ZoneCode=" + ZoneCode);
      if (YearCode != "" && ZoneCode != "") {
        this.DesignationLomCodeOptionsLoading = true;
        var selectbox1_source = "DesignationLomCode";
        var selectbox1_destination = "DesignationLomCodeOptions";
        var selectbox1_url = "api/year-wise-lom/lom-options";
        var selectbox1_conditions_array = {
          UserInterface: 1,
          YearCode: YearCode,
          ZoneCode: ZoneCode,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      } else {
        var message = "";
        if (YearCode == "") {
          message += "Year should not be empty. ";
        }
        if (ZoneCode == "") {
          message += "Zone should not be empty. ";
        }
        this.alert = {
          flag: true,
          color: "error",
          message: message,
        };
      }
    },
    getDesignationCodeOptions() {
      console.log("getDesignationCodeOptions called");
      this.DesignationAreaCode = "";
      this.DesignationZoneCode = "";
      this.DesignationLomCode = "";
      this.DesignationCodeOptionsLoading = true;
      var selectbox1_source = "DesignationCode";
      var selectbox1_destination = "DesignationCodeOptions";
      var selectbox1_url = "api/designation/options";
      var selectbox1_conditions_array = {
        UserInterface: 3,
        OrganizationTypeId: this.OrganizationType,
        AllOption: 0,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    resetForm() {
      // form reset also clears the mode radio, so the selected mode is kept
      var Mode = this.Mode;
      this.$refs.form1.reset();
      this.Mode = Mode;
      this.TypeCode = "";
      this.MemberType = "";
      this.ZoneCode = "";
      this.ZoneCodes = [];
      this.OrganizationType = "";
      this.Subject = "";
      this.Content = "";
      this.CCMails = "";
    },
    submitForm() {
      console.log("submitForm is called");

      var validate1 = this.$refs.form1.validate();
      console.log("validate1=" + validate1);

      if (validate1 && this.Mode == 2) {
        const typeFormData = new FormData();

        typeFormData.append("UserInterface", 2);
        typeFormData.append("Year", this.JciYearCode);
        typeFormData.append("Type", this.TypeCode);
        typeFormData.append("SendTo", this.SendTo);
        typeFormData.append("Recipient", this.Recipient || "");
        typeFormData.append("Zone", this.ZoneCode || "");
        if ([2, 4].includes(this.SendTo)) {
          for (var z = 0; z < this.ZoneCodes.length; z++) {
            typeFormData.append("Zones[]", this.ZoneCodes[z]);
          }
        }
        if (this.SendTo == 3) {
          for (var k = 0; k < this.LomCode.length; k++) {
            typeFormData.append("Lom[]", this.LomCode[k]);
          }
          typeFormData.append("YearHalf", this.YearHalf || "");
        }
        if (this.SendTo == 5) {
          for (var t = 0; t < this.TableCode.length; t++) {
            typeFormData.append("Table[]", this.TableCode[t]);
          }
        }
        typeFormData.append("ReplyTo", this.ReplyToEmailId);
        typeFormData.append("Subject", this.Subject);
        typeFormData.append("Content", this.Content);
        typeFormData.append("CCMails", this.CCMails);
        if (this.UploadedFile !== "") {
          typeFormData.append("UploadFile", this.UploadedFile);
        }
        if (this.UploadedFile1 !== "") {
          typeFormData.append("UploadFile1", this.UploadedFile1);
        }
        this.sendEmail(typeFormData);
      } else if (validate1) {
        const formData = new FormData();

        formData.append("UserInterface", 1);
        formData.append("Year", this.JciYearCode);
        formData.append("MemberType", this.MemberType);
        formData.append("Zone", this.ZoneCode);

        for (var i = 0; i < this.LomCode.length; i++) {
          formData.append("Lom[]", this.LomCode[i]);
        }
        // formData.append("Lom", this.LomCode);
        formData.append("YearHalf", this.YearHalf);
        formData.append("OrganizationType", this.OrganizationType);
        formData.append("OrganizationType1", this.OrganizationType1);

        for (var j = 0; j < this.DesignationCode.length; j++) {
          formData.append("Designation[]", this.DesignationCode[j]);
        }
        // formData.append("Designation", this.DesignationCode);
        formData.append("ReplyTo", this.ReplyToEmailId);
        formData.append("Subject", this.Subject);
        formData.append("Content", this.Content);
        formData.append("CCMails", this.CCMails);
        formData.append("DesignationArea", this.DesignationAreaCode);
        formData.append("DesignationZone", this.DesignationZoneCode);
        formData.append("DesignationLom", this.DesignationLomCode);
        if (this.UploadedFile !== "") {
          formData.append("UploadFile", this.UploadedFile);
        }
        if (this.UploadedFile1 !== "") {
          formData.append("UploadFile1", this.UploadedFile1);
        }
        this.sendEmail(formData);
      } else {
        var message = "";
        if (!validate1) {
          message += "Kindly fill the required fields";
        }
        this.sweetAlert("error", message, false);
      }
    },
    sendEmail(upload) {
      console.log("sendEmail is called");

      console.log({ upload });

      this.SubmitFlag = true;
      this.ProgressLoadingFlag = true;

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/communication/send-members-email";
      console.log(
        "server_url=" + server_url + ", token=" + token + ", add_url=" + add_url
      );

      const thisIns = this;
      var output = "";
      var records = "";
      var flag = 0;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        onUploadProgress: function (progressEvent) {
          this.uploadPercentage = parseInt(
            Math.round((progressEvent.loaded / progressEvent.total) * 100)
          );
        }.bind(this),
      })
        .then(function (response) {
          console.log("response=" + JSON.stringify(response));

          thisIns.SubmitFlag = false;
          thisIns.ProgressLoadingFlag = false;

          output = response.data.output;
          flag = response.data.flag;
          console.log("output=" + output + ", flag=" + flag);

          records = response.data.records;
          console.log({ records });

          var EmailCredits = records.EmailCredits;
          console.log({ EmailCredits });

          thisIns.EmailCredits = EmailCredits;

          if (flag == 1) {
            thisIns.sweetAlert("success", output, false);
            // thisIns.refreshPageData();
            thisIns.resetForm();
          } else {
            thisIns.sweetAlert("error", output, false);
          }
        })
        .catch(function (error) {
          console.log("error=" + error);
          thisIns.SubmitFlag = false;
          thisIns.ProgressLoadingFlag = false;
        });
    },
    getOrganizationTypeOptions() {
      console.log("getOrganizationTypeOptions called");
      this.OrganizationTypeOptionsLoading1 = true;
      this.OrganizationType1 = "";
      var selectbox1_source = "OrganizationType1";
      var selectbox1_destination = "OrganizationTypeOptions1";
      var selectbox1_url = "api/organization-type/options";
      var selectbox1_conditions_array = {
        UserInterface: 1,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>
<style lang="scss">
#send-members-email {
  /* JCI approved palette */
  --navy: #3a67b1;
  --navy-deep: #1f3f78;
  --ink: #0a0f29;
  --aqua: #0097d7;
  --gold: #edbe38;
  --seafoam: #56bda3;
  --text: #414042;
  --white: #ffffff;

  --surface: #ffffff;
  --surface-tint: #f5f8fd;
  --line: #e3e9f3;
  --line-strong: #cdd8ea;

  --radius-lg: 22px;
  --radius-md: 14px;
  --radius-sm: 10px;

  --ease: cubic-bezier(0.22, 0.61, 0.36, 1);
  --ease-out: cubic-bezier(0.16, 1, 0.3, 1);
  --t-fast: 180ms;
  --t-med: 280ms;

  --shadow-sm: 0 1px 2px rgba(10, 15, 41, 0.04),
    0 4px 14px rgba(31, 63, 120, 0.06);
  --shadow-md: 0 2px 4px rgba(10, 15, 41, 0.04),
    0 14px 34px rgba(31, 63, 120, 0.1);

  position: relative;
  isolation: isolate;
  padding: 4px 0 32px;
  color: var(--text);
  font-family: "Poppins", "Roboto", sans-serif;

  /* legacy helpers kept for compatibility */
  .notes {
    color: red;
    font-size: 18px;
    font-weight: 500;
    line-height: 2;
  }
  .notes-title {
    font-size: 20px;
    font-weight: bold;
    line-height: 2em;
  }
  .notes-content {
    font-size: 20px;
    font-weight: bold;
    line-height: 2em;
  }
  .notes-text1 {
    padding: 5px;
    margin-left: 2rem;
    font-size: 14px;
    font-family: sans-serif;
  }

  /* ---------- Ambient background ---------- */
  .sme-ambient {
    position: absolute;
    inset: 0;
    z-index: -1;
    overflow: hidden;
    pointer-events: none;
    border-radius: 28px;
  }
  .sme-glow {
    position: absolute;
    border-radius: 50%;
    filter: blur(70px);
    opacity: 0.35;
    animation: sme-drift 26s ease-in-out infinite alternate;
  }
  .sme-glow--navy {
    width: 420px;
    height: 420px;
    top: 260px;
    left: -140px;
    background: rgba(58, 103, 177, 0.35);
  }
  .sme-glow--aqua {
    width: 380px;
    height: 380px;
    top: 720px;
    right: -120px;
    background: rgba(0, 151, 215, 0.28);
    animation-delay: -9s;
  }
  .sme-glow--seafoam {
    width: 300px;
    height: 300px;
    bottom: 40px;
    left: 30%;
    background: rgba(86, 189, 163, 0.22);
    animation-delay: -16s;
  }

  /* ---------- Entrance reveal ---------- */
  .sme-reveal {
    animation: sme-rise 640ms var(--ease-out) backwards;
    animation-delay: var(--d, 0ms);
  }

  /* ---------- Hero ---------- */
  .sme-hero {
    position: relative;
    overflow: hidden;
    border-radius: 28px;
    padding: 36px 40px;
    margin-bottom: 26px;
    color: var(--white);
    background: radial-gradient(
        120% 140% at 100% 0%,
        rgba(0, 151, 215, 0.75) 0%,
        rgba(0, 151, 215, 0) 55%
      ),
      radial-gradient(
        80% 120% at 0% 100%,
        rgba(86, 189, 163, 0.35) 0%,
        rgba(86, 189, 163, 0) 60%
      ),
      linear-gradient(125deg, var(--navy) 0%, var(--navy) 50%, var(--aqua) 100%);
    box-shadow: 0 24px 60px -24px rgba(58, 103, 177, 0.6);
  }
  .sme-hero::after {
    /* fine grid texture */
    content: "";
    position: absolute;
    inset: 0;
    background-image: linear-gradient(
        rgba(255, 255, 255, 0.05) 1px,
        transparent 1px
      ),
      linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
    background-size: 36px 36px;
    mask-image: linear-gradient(90deg, transparent 0%, #000 70%);
    -webkit-mask-image: linear-gradient(90deg, transparent 0%, #000 70%);
    pointer-events: none;
  }
  .sme-hero__decor {
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 0;
  }
  .sme-orb {
    position: absolute;
    border-radius: 50%;
    filter: blur(40px);
    animation: sme-drift 18s ease-in-out infinite alternate;
  }
  .sme-orb--1 {
    width: 260px;
    height: 260px;
    right: 18%;
    bottom: -140px;
    background: rgba(86, 189, 163, 0.35);
  }
  .sme-orb--2 {
    width: 180px;
    height: 180px;
    left: 38%;
    top: -90px;
    background: rgba(237, 190, 56, 0.18);
    animation-delay: -7s;
  }
  .sme-ring {
    position: absolute;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 0.12);
  }
  .sme-ring--1 {
    width: 340px;
    height: 340px;
    right: -90px;
    top: -150px;
    animation: sme-spin 60s linear infinite;
    border-style: dashed;
  }
  .sme-ring--2 {
    width: 220px;
    height: 220px;
    right: -30px;
    top: -90px;
  }
  .sme-particle {
    position: absolute;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.7);
    box-shadow: 0 0 12px rgba(0, 151, 215, 0.9);
    animation: sme-float 9s ease-in-out infinite;
  }
  .sme-particle--1 {
    left: 52%;
    top: 22%;
  }
  .sme-particle--2 {
    left: 61%;
    top: 70%;
    width: 4px;
    height: 4px;
    animation-delay: -2s;
  }
  .sme-particle--3 {
    left: 8%;
    top: 82%;
    width: 5px;
    height: 5px;
    animation-delay: -4s;
  }
  .sme-particle--4 {
    left: 44%;
    top: 84%;
    background: var(--gold);
    box-shadow: 0 0 12px rgba(237, 190, 56, 0.8);
    width: 4px;
    height: 4px;
    animation-delay: -6s;
  }
  .sme-particle--5 {
    left: 30%;
    top: 12%;
    width: 3px;
    height: 3px;
    animation-delay: -3s;
  }
  .sme-hero__flight {
    position: absolute;
    left: 30%;
    bottom: 8px;
    width: 46%;
    max-width: 520px;
    color: var(--white);
    filter: drop-shadow(0 0 4px rgba(255, 255, 255, 0.9))
      drop-shadow(0 0 10px rgba(0, 151, 215, 0.8));
  }
  .sme-hero__flight path {
    animation: sme-dash 14s linear infinite, sme-blink 2.4s ease-in-out infinite;
  }
  .sme-hero__flight .sme-hero__flight-light {
    /* bright light travelling along the flight path */
    stroke-dasharray: 6 94;
    filter: drop-shadow(0 0 6px var(--white)) drop-shadow(0 0 14px var(--gold));
    animation: sme-flight-light 4.5s linear infinite;
  }

  .sme-hero__grid {
    position: relative;
    z-index: 1;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 32px;
    align-items: center;
  }
  .sme-hero__side {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 18px;
  }
  .sme-hero__copy {
    min-width: 0;
  }
  .sme-hero__eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    font-size: 0.78rem;
    font-weight: 600;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.78);
    margin-bottom: 14px;

    em {
      font-family: "Lora", Georgia, serif;
      font-style: italic;
      font-weight: 700;
      letter-spacing: 0.04em;
      text-transform: none;
      font-size: 1.05rem;
      color: var(--gold);
    }
  }
  .sme-hero__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    border-radius: 13px;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.22),
      rgba(255, 255, 255, 0.06)
    );
    border: 1px solid rgba(255, 255, 255, 0.25);
    backdrop-filter: blur(6px);
    animation: sme-pop 700ms var(--ease-out) 200ms backwards;
  }
  .sme-hero__title {
    margin: 0;
    font-size: clamp(1.6rem, 1.1rem + 1.8vw, 2.6rem);
    font-weight: 700;
    line-height: 1.15;
    letter-spacing: -0.01em;
    color: var(--white);
    overflow-wrap: anywhere;
  }
  .sme-hero__accent {
    display: block;
    width: 96px;
    height: 3px;
    margin: 16px 0 14px;
    border-radius: 3px;
    background: linear-gradient(
      90deg,
      var(--gold),
      var(--aqua),
      var(--seafoam),
      var(--gold)
    );
    background-size: 300% 100%;
    animation: sme-gradient 6s linear infinite,
      sme-grow 900ms var(--ease-out) 300ms both;
    transform-origin: left center;
  }
  .sme-hero__desc {
    margin: 0;
    max-width: 640px;
    font-size: 0.98rem;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.78);
  }

  /* ---------- Credit card ---------- */
  .sme-credit {
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: center;
    gap: 18px;
    min-width: 280px;
    padding: 22px 26px;
    border-radius: 22px;
    background: linear-gradient(
      150deg,
      rgba(58, 103, 177, 0.95),
      rgba(10, 15, 41, 0.75)
    );
    border: 1px solid rgba(255, 255, 255, 0.22);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.12),
      0 20px 40px -18px rgba(0, 0, 0, 0.6);
    transition: transform var(--t-med) var(--ease),
      box-shadow var(--t-med) var(--ease);

    &:hover {
      transform: translateY(-4px);
      box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.16),
        0 28px 50px -18px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(0, 151, 215, 0.4);
    }
    &:hover .sme-credit__icon {
      transform: rotate(-8deg) scale(1.05);
    }
  }
  .sme-credit__shine {
    position: absolute;
    top: 0;
    left: -60%;
    width: 50%;
    height: 100%;
    background: linear-gradient(
      100deg,
      transparent 0%,
      rgba(255, 255, 255, 0.14) 50%,
      transparent 100%
    );
    transform: skewX(-18deg);
    animation: sme-shine 6.5s var(--ease) infinite 1.2s;
    pointer-events: none;
  }
  .sme-credit__rings {
    position: absolute;
    right: -30px;
    bottom: -38px;
    width: 140px;
    height: 140px;
    color: rgba(0, 151, 215, 0.35);
    animation: sme-spin 40s linear infinite;
    pointer-events: none;
  }
  .sme-credit__icon {
    position: relative;
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    border-radius: 16px;
    background: var(--gold);
    box-shadow: 0 8px 22px -6px rgba(237, 190, 56, 0.65);
    transition: transform var(--t-med) var(--ease);

    &::after {
      content: "";
      position: absolute;
      inset: -6px;
      border-radius: 20px;
      border: 1px solid rgba(237, 190, 56, 0.5);
      animation: sme-pulse 2.8s var(--ease) infinite;
    }
  }
  .sme-credit__body {
    position: relative;
    min-width: 0;
  }
  .sme-credit__label {
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.7);
  }
  .sme-credit__value {
    font-size: clamp(2rem, 1.6rem + 1.4vw, 2.75rem);
    font-weight: 700;
    line-height: 1.1;
    letter-spacing: -0.02em;
    color: var(--white);
    font-variant-numeric: tabular-nums;
    overflow-wrap: anywhere;
  }
  .sme-credit__hint {
    font-size: 0.8rem;
    color: var(--seafoam);
  }

  /* ---------- Loading overlay ---------- */
  .sme-overlay-loader {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .sme-overlay-icon {
    position: absolute;
    animation: sme-float 2.4s ease-in-out infinite;
  }

  /* ---------- Panels ---------- */
  .sme-form {
    display: flex;
    flex-direction: column;
    gap: 22px;
  }
  .sme-panel {
    position: relative;
    padding: 28px 30px 22px;
    border-radius: var(--radius-lg);
    background: var(--surface);
    border: 1px solid var(--line);
    box-shadow: var(--shadow-sm);
    transition: box-shadow var(--t-med) var(--ease),
      border-color var(--t-med) var(--ease);

    &::before {
      /* asymmetric accent edge */
      content: "";
      position: absolute;
      left: -1px;
      top: 28px;
      width: 4px;
      height: 46px;
      border-radius: 0 4px 4px 0;
      background: linear-gradient(180deg, var(--navy), var(--aqua));
      transition: height var(--t-med) var(--ease);
    }
    &:hover {
      box-shadow: var(--shadow-md);
      border-color: var(--line-strong);
    }
    &:hover::before {
      height: 72px;
    }
  }
  .sme-panel--targeting {
    background: linear-gradient(180deg, #ffffff 0%, var(--surface-tint) 100%);
  }
  .sme-panel--compose::before {
    background: linear-gradient(180deg, var(--aqua), var(--seafoam));
  }
  .sme-panel--dispatch {
    background: linear-gradient(
      160deg,
      #ffffff 40%,
      rgba(86, 189, 163, 0.08) 100%
    );

    &::before {
      background: linear-gradient(180deg, var(--seafoam), var(--gold));
    }
  }
  .sme-panel__head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    margin-bottom: 18px;
  }

  .sme-section-title {
    display: flex;
    align-items: center;
    gap: 14px;
    min-width: 0;

    h2 {
      position: relative;
      margin: 0;
      padding-bottom: 6px;
      font-size: 1.15rem;
      font-weight: 600;
      color: var(--ink);
      display: inline-block;

      &::after {
        content: "";
        position: absolute;
        left: 0;
        bottom: 0;
        width: 28px;
        height: 2px;
        border-radius: 2px;
        background: var(--aqua);
        transition: width var(--t-med) var(--ease);
      }
    }
    p {
      margin: 2px 0 0;
      font-size: 0.85rem;
      color: #6b7185;
    }
  }
  .sme-panel:hover .sme-section-title h2::after,
  .sme-panel:focus-within .sme-section-title h2::after {
    width: 100%;
  }
  .sme-step {
    font-family: "Lora", Georgia, serif;
    font-style: italic;
    font-weight: 700;
    font-size: 1.5rem;
    line-height: 1;
    color: transparent;
    -webkit-text-stroke: 1px var(--line-strong);
    transition: color var(--t-med) var(--ease),
      -webkit-text-stroke-color var(--t-med) var(--ease);
  }
  .sme-panel:hover .sme-step,
  .sme-panel:focus-within .sme-step {
    color: var(--gold);
    -webkit-text-stroke-color: var(--gold);
  }
  .sme-section-title__icon {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    border-radius: 12px;
    background: rgba(58, 103, 177, 0.08);
    border: 1px solid rgba(58, 103, 177, 0.14);
    transition: transform var(--t-med) var(--ease),
      background var(--t-med) var(--ease);
    animation: sme-pop 600ms var(--ease-out) backwards;
    animation-delay: calc(var(--d, 0ms) + 220ms);
  }
  .sme-panel:hover .sme-section-title__icon {
    transform: translateY(-2px) rotate(-6deg);
    background: rgba(0, 151, 215, 0.12);
  }

  .sme-required-note {
    margin: -6px 0 10px;
    font-size: 0.8rem;
    color: #6b7185;
  }
  .sme-req {
    color: #e53935;
    font-weight: 700;
  }
  .sme-badge {
    display: inline-block;
    margin-left: 6px;
    padding: 1px 8px;
    border-radius: 999px;
    font-size: 0.68rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    vertical-align: middle;
    color: var(--navy);
    background: rgba(58, 103, 177, 0.1);
  }

  /* ---------- Mode segmented control ---------- */
  .sme-mode {
    position: relative;
    padding: 4px;
    border-radius: 14px;
    background: #eef2f9;
    border: 1px solid var(--line);
    min-width: 280px;

    .v-input--radio-group {
      padding-top: 0;
    }
    .v-input--radio-group__input {
      flex-wrap: nowrap;
    }
    .v-radio {
      position: relative;
      z-index: 1;
      flex: 1 1 0;
      margin: 0 !important;
      justify-content: center;
    }
    /* keep the native radio focusable but visually replace it with the pill */
    .v-input--selection-controls__input {
      position: absolute;
      width: 1px;
      height: 1px;
      margin: 0;
      opacity: 0;
      overflow: hidden;
    }
    .v-label {
      flex: 1 1 auto;
      justify-content: center;
      gap: 6px;
      padding: 9px 18px;
      border-radius: 10px;
      font-size: 0.9rem;
      font-weight: 500;
      color: #5b6178 !important;
      white-space: nowrap;
      transition: color var(--t-med) var(--ease);
    }
    .sme-mode__icon {
      color: inherit !important;
      transition: transform var(--t-med) var(--ease);
    }
    .v-radio:hover .v-label {
      color: var(--navy) !important;
    }
    .v-radio.v-item--active .v-label {
      color: var(--white) !important;
    }
    .v-radio.v-item--active .sme-mode__icon {
      transform: scale(1.1);
    }
    .v-radio--is-focused .v-label {
      box-shadow: 0 0 0 2px rgba(0, 151, 215, 0.6);
    }
  }
  .sme-mode__indicator {
    position: absolute;
    top: 4px;
    bottom: 4px;
    left: 4px;
    width: calc(50% - 4px);
    border-radius: 10px;
    background: linear-gradient(135deg, var(--navy), var(--aqua));
    box-shadow: 0 6px 16px -6px rgba(0, 151, 215, 0.7);
    transition: transform 320ms var(--ease-out);
  }
  .sme-mode--2 .sme-mode__indicator {
    transform: translateX(100%);
  }

  /* ---------- Fields ---------- */
  .sme-fields {
    margin-top: 0;
  }
  .sme-field {
    padding-top: 6px;
    padding-bottom: 2px;
    animation: sme-field-in 340ms var(--ease-out) backwards;
  }
  .sme-label {
    display: block;
    margin-bottom: 6px;
    font-size: 0.78rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #4a5068;
  }

  .v-text-field--outlined {
    > .v-input__control > .v-input__slot {
      min-height: 44px;
      border-radius: var(--radius-sm);
      background: #f7f9fd;
      transition: background var(--t-fast) var(--ease),
        box-shadow var(--t-med) var(--ease);
    }
    fieldset {
      border-radius: var(--radius-sm);
      transition: border-color var(--t-fast) var(--ease),
        border-width var(--t-fast) var(--ease);
    }
    &:not(.v-input--has-state) fieldset {
      border-color: var(--line-strong);
    }
    &:not(.v-input--is-focused):not(.v-input--has-state)
      > .v-input__control
      > .v-input__slot:hover {
      background: #ffffff;
    }
    &:not(.v-input--is-focused):not(.v-input--has-state)
      > .v-input__control
      > .v-input__slot:hover
      fieldset {
      border-color: rgba(58, 103, 177, 0.55);
    }
    &.v-input--is-focused:not(.v-input--has-state) {
      > .v-input__control > .v-input__slot {
        background: #ffffff;
        box-shadow: 0 0 0 4px rgba(0, 151, 215, 0.12);
      }
      fieldset {
        border-color: var(--aqua);
        border-width: 1.5px;
      }
    }
    &.v-input--has-state.error--text > .v-input__control > .v-input__slot {
      box-shadow: 0 0 0 4px rgba(229, 57, 53, 0.08);
    }
    &.v-input--is-disabled > .v-input__control > .v-input__slot {
      background: #eef1f6;
    }
    .v-select__selections {
      color: var(--ink);
      font-weight: 500;
    }
    .v-input__icon .v-icon {
      transition: transform var(--t-med) var(--ease),
        color var(--t-fast) var(--ease);
    }
    &.v-input--is-focused .v-input__prepend-inner .v-icon {
      color: var(--aqua);
    }
    .v-progress-linear {
      border-radius: 0 0 var(--radius-sm) var(--radius-sm);
    }
  }
  .v-select.v-select--is-menu-active .v-input__append-inner .v-icon {
    color: var(--aqua);
  }
  .v-select__selections .v-chip {
    border-radius: 8px;
    background: rgba(58, 103, 177, 0.1) !important;
    color: var(--navy-deep);
    font-weight: 500;
  }
  .v-messages {
    font-size: 0.74rem;
  }

  /* subject: prominent field */
  .sme-field--subject {
    .v-text-field--outlined > .v-input__control > .v-input__slot {
      min-height: 54px;
    }
    input {
      font-size: 1.15rem;
      font-weight: 500;
      color: var(--ink);
    }
  }

  /* ---------- Compose subgroups ---------- */
  .sme-subgroup {
    padding: 18px 0 6px;
    border-top: 1px dashed var(--line);

    &:first-of-type {
      border-top: 0;
      padding-top: 0;
    }
  }
  .sme-subgroup__title {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--navy);

    .v-icon {
      color: var(--aqua);
    }
  }

  /* ---------- Personalization panel ---------- */
  .sme-vars {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 14px 22px;
    padding: 14px 18px;
    margin-bottom: 16px;
    border-radius: var(--radius-md);
    background: linear-gradient(
      135deg,
      rgba(0, 151, 215, 0.07),
      rgba(86, 189, 163, 0.07)
    );
    border: 1px solid rgba(0, 151, 215, 0.18);
    overflow: hidden;

    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 0;
      height: 2px;
      width: 100%;
      background: linear-gradient(
        90deg,
        transparent,
        var(--aqua),
        var(--seafoam),
        transparent
      );
      background-size: 200% 100%;
      animation: sme-gradient 5s linear infinite;
    }
  }
  .sme-vars__head {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }
  .sme-vars__icon {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 10px;
    background: #ffffff;
    box-shadow: 0 2px 8px rgba(0, 151, 215, 0.18);
  }
  .sme-vars__title {
    font-size: 0.9rem;
    font-weight: 600;
    color: var(--ink);
  }
  .sme-vars__sub {
    font-size: 0.78rem;
    color: #6b7185;
  }
  .sme-vars__list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin: 0 0 0 auto;
    padding: 0;
    list-style: none;
  }
  .sme-var {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 4px 4px 4px 10px;
    border-radius: 10px;
    background: #ffffff;
    border: 1px solid var(--line);
    transition: transform var(--t-fast) var(--ease),
      border-color var(--t-fast) var(--ease),
      box-shadow var(--t-fast) var(--ease);

    &:hover {
      transform: translateY(-2px);
      border-color: rgba(0, 151, 215, 0.45);
      box-shadow: 0 6px 14px -6px rgba(0, 151, 215, 0.45);
    }
  }
  .sme-var__label {
    font-size: 0.75rem;
    color: #6b7185;
  }
  .sme-var__code {
    padding: 3px 8px;
    border-radius: 7px;
    font-family: "SFMono-Regular", Consolas, "Liberation Mono", monospace;
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--navy-deep);
    background: rgba(58, 103, 177, 0.08);
    box-shadow: none;
    user-select: all;

    &::before,
    &::after {
      content: none;
    }
  }

  /* ---------- CKEditor container ---------- */
  .sme-editor {
    border-radius: var(--radius-md);
    border: 1px solid var(--line-strong);
    background: #ffffff;
    overflow: hidden;
    transition: border-color var(--t-fast) var(--ease),
      box-shadow var(--t-med) var(--ease);

    &:hover {
      border-color: rgba(58, 103, 177, 0.5);
    }
    &:focus-within {
      border-color: var(--aqua);
      box-shadow: 0 0 0 4px rgba(0, 151, 215, 0.12);
    }
    .cke_chrome {
      border: 0 !important;
      box-shadow: none !important;
    }
    .cke_top {
      background: var(--surface-tint) !important;
      border-bottom: 1px solid var(--line) !important;
      box-shadow: none !important;
      padding: 8px 8px 4px !important;
    }
    .cke_bottom {
      background: var(--surface-tint) !important;
      border-top: 1px solid var(--line) !important;
    }
    .cke_toolgroup {
      border-radius: 6px;
    }
  }

  /* ---------- Attachments ---------- */
  .sme-attach {
    position: relative;
    height: 100%;
    padding: 16px 18px 6px;
    border-radius: var(--radius-md);
    border: 1.5px dashed var(--line-strong);
    background: rgba(255, 255, 255, 0.8);
    transition: border-color var(--t-med) var(--ease),
      background var(--t-med) var(--ease), transform var(--t-med) var(--ease),
      box-shadow var(--t-med) var(--ease);

    &:hover {
      border-color: var(--aqua);
      background: #ffffff;
      transform: translateY(-3px);
      box-shadow: var(--shadow-md);
    }
    &:hover .sme-attach__icon {
      transform: translateY(-3px);
      background: rgba(0, 151, 215, 0.12);
    }
    .v-file-input {
      margin-top: 4px;
      padding-top: 0;
    }
    .v-input__prepend-outer .v-icon {
      color: var(--navy);
      transition: transform var(--t-med) var(--ease);
    }
    &:hover .v-input__prepend-outer .v-icon {
      transform: rotate(-20deg);
    }
    .v-input__slot {
      min-height: 40px;
    }
    .v-file-input__text {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      color: var(--ink);
      font-weight: 500;
    }
    .v-input__append-outer .v-icon {
      padding: 6px;
      margin-top: -6px;
      border-radius: 8px;
      transition: background var(--t-fast) var(--ease),
        transform var(--t-fast) var(--ease);

      &:hover {
        background: rgba(229, 57, 53, 0.1);
        transform: scale(1.08);
      }
    }
  }
  .sme-attach--filled {
    border-style: solid;
    border-color: rgba(86, 189, 163, 0.7);
    background: linear-gradient(160deg, #ffffff, rgba(86, 189, 163, 0.07));

    .sme-attach__icon {
      background: rgba(86, 189, 163, 0.16);
    }
  }
  .sme-attach__head {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .sme-attach__icon {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: rgba(58, 103, 177, 0.08);
    transition: transform var(--t-med) var(--ease),
      background var(--t-med) var(--ease);
  }
  .sme-attach__meta {
    min-width: 0;
  }
  .sme-attach__title {
    font-size: 0.95rem;
    font-weight: 600;
    color: var(--ink);
  }
  .sme-attach__hint {
    font-size: 0.78rem;
    color: #6b7185;
  }

  /* ---------- Send bar ---------- */
  .sme-send-bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-top: 18px;
    padding-top: 18px;
    border-top: 1px dashed var(--line);
  }
  .sme-send-bar__copy {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 0.85rem;
    color: #6b7185;
    min-width: 0;
  }

  .sme-send-btn.v-btn {
    position: relative;
    overflow: hidden;
    min-width: 200px;
    height: 52px !important;
    padding: 0 30px !important;
    border-radius: 14px;
    font-size: 0.98rem;
    font-weight: 600;
    letter-spacing: 0.03em;
    text-transform: none;
    color: var(--white) !important;
    transition: transform var(--t-med) var(--ease),
      box-shadow var(--t-med) var(--ease), background-position 600ms var(--ease);

    &:not(.v-btn--disabled) {
      background-image: linear-gradient(
        120deg,
        var(--navy) 0%,
        var(--aqua) 50%,
        var(--navy) 100%
      );
      background-color: var(--navy) !important;
      background-size: 200% 100%;
      background-position: 0% 0%;
      box-shadow: 0 12px 28px -10px rgba(0, 151, 215, 0.75);
    }
    &:not(.v-btn--disabled):hover {
      background-position: 100% 0%;
      transform: translateY(-2px);
      box-shadow: 0 18px 36px -12px rgba(0, 151, 215, 0.9),
        0 0 0 4px rgba(0, 151, 215, 0.14);
    }
    &:not(.v-btn--disabled):hover .sme-send-btn__icon {
      transform: translateX(4px) rotate(-12deg);
    }
    &:not(.v-btn--disabled):active {
      transform: translateY(0) scale(0.98);
      box-shadow: 0 6px 16px -8px rgba(0, 151, 215, 0.8);
    }
    &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 4px rgba(0, 151, 215, 0.35);
    }
    &.v-btn--disabled {
      background: #dfe4ee !important;
      color: #8a90a3 !important;
      box-shadow: none;

      .v-icon {
        color: #8a90a3 !important;
      }
      .sme-send-btn__shine {
        display: none;
      }
    }
    &.v-btn--loading {
      cursor: progress;
    }
    &::before {
      /* disable Vuetify's default hover tint over the gradient */
      opacity: 0 !important;
    }
    .v-btn__loader {
      font-weight: 500;
      color: var(--white);
    }
  }
  .sme-send-btn__icon {
    color: var(--white) !important;
    transition: transform var(--t-med) var(--ease);
  }
  .sme-send-btn__shine {
    position: absolute;
    top: 0;
    left: -75%;
    width: 50%;
    height: 100%;
    background: linear-gradient(
      100deg,
      transparent 0%,
      rgba(255, 255, 255, 0.35) 50%,
      transparent 100%
    );
    transform: skewX(-20deg);
    animation: sme-shine 4.5s var(--ease) infinite 1.5s;
    pointer-events: none;
  }

  /* ---------- Responsive ---------- */
  @media (max-width: 959px) {
    .sme-hero {
      padding: 28px 26px;
    }
    .sme-hero__grid {
      grid-template-columns: minmax(0, 1fr);
      gap: 22px;
    }
    .sme-credit {
      min-width: 0;
    }
    .sme-hero__flight {
      display: none;
    }
    .sme-vars__list {
      margin-left: 0;
    }
  }
  @media (max-width: 599px) {
    padding-bottom: 20px;

    .sme-hero {
      padding: 24px 18px;
      border-radius: 22px;
    }
    .sme-panel {
      padding: 22px 16px 16px;
    }
    .sme-panel__head {
      flex-direction: column;
      align-items: stretch;
    }
    .sme-mode {
      min-width: 0;
      width: 100%;
    }
    .sme-step {
      display: none;
    }
    .sme-send-bar {
      flex-direction: column;
      align-items: stretch;
    }
    .sme-send-btn.v-btn {
      width: 100%;
    }
    .sme-var {
      flex: 1 1 calc(50% - 8px);
      justify-content: space-between;
    }
  }

  /* ---------- Reduced motion ---------- */
  @media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
      animation-duration: 1ms !important;
      animation-iteration-count: 1 !important;
      animation-delay: 0ms !important;
      transition-duration: 1ms !important;
    }
    .sme-credit__shine,
    .sme-send-btn__shine,
    .sme-hero__flight-light,
    .sme-particle {
      display: none;
    }
  }
}

/* ---------- Keyframes ---------- */
@keyframes sme-rise {
  from {
    opacity: 0;
    transform: translateY(18px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
@keyframes sme-field-in {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
@keyframes sme-pop {
  from {
    opacity: 0;
    transform: scale(0.6) rotate(-12deg);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
@keyframes sme-grow {
  from {
    transform: scaleX(0);
  }
  to {
    transform: scaleX(1);
  }
}
@keyframes sme-gradient {
  from {
    background-position: 0% 50%;
  }
  to {
    background-position: 300% 50%;
  }
}
@keyframes sme-drift {
  0% {
    transform: translate(0, 0) scale(1);
  }
  50% {
    transform: translate(30px, -20px) scale(1.06);
  }
  100% {
    transform: translate(-20px, 25px) scale(0.96);
  }
}
@keyframes sme-float {
  0%,
  100% {
    transform: translateY(0);
    opacity: 0.85;
  }
  50% {
    transform: translateY(-12px);
    opacity: 0.4;
  }
}
@keyframes sme-spin {
  to {
    transform: rotate(360deg);
  }
}
@keyframes sme-dash {
  to {
    stroke-dashoffset: -240;
  }
}
@keyframes sme-blink {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.45;
  }
}
@keyframes sme-flight-light {
  from {
    stroke-dashoffset: 100;
  }
  to {
    stroke-dashoffset: 0;
  }
}
@keyframes sme-shine {
  0% {
    left: -75%;
  }
  35%,
  100% {
    left: 130%;
  }
}
@keyframes sme-pulse {
  0% {
    opacity: 0.9;
    transform: scale(0.92);
  }
  70%,
  100% {
    opacity: 0;
    transform: scale(1.18);
  }
}
</style>
